import { Button, Tooltip } from 'antd'
import { CloseOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

/**
 * Shared top header for the three stage editors (抠图编辑器 / 色底图编辑器 /
 * 阴影图编辑器) — one place that owns the "标题 · dirty 状态 · 取消 · 恢复默认
 * · 保存 · 关闭" layout so all three editors look and behave identically
 * instead of each hand-rolling its own header/footer combination.
 */
export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

interface Props {
  title: string
  dirty: boolean
  saving?: boolean
  saveError?: string
  saveLabel?: string
  onCancel: () => void
  onReset: () => void
  onSave: () => void
  onClose: () => void
  saveDisabled?: boolean
  cancelDisabled?: boolean
  extra?: React.ReactNode
  testIdPrefix: string
}

export default function EditorHeader({
  title,
  dirty,
  saving = false,
  saveError = '',
  saveLabel,
  onCancel,
  onReset,
  onSave,
  onClose,
  saveDisabled = false,
  cancelDisabled,
  extra,
  testIdPrefix,
}: Props) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const p = testIdPrefix

  return (
    <header className="edhdr-header" data-testid={`${p}-header`}>
      <span className="edhdr-title" title={title}>
        {title}
      </span>
      {saveError ? (
        <span className="edhdr-error-tag" data-testid={`${p}-save-error-tag`}>
          {t('editorHeader.saveFailed', { message: saveError })}
        </span>
      ) : dirty ? (
        <span className="edhdr-dirty-tag" data-testid={`${p}-dirty-tag`}>
          {t('editorHeader.unsaved')}
        </span>
      ) : (
        <span className="edhdr-saved-tag" data-testid={`${p}-saved-tag`}>
          {t('editorHeader.saved')}
        </span>
      )}
      <div className="edhdr-spacer" />
      <div className="edhdr-actions">
        {extra}
        <Button
          size="small"
          onClick={onCancel}
          disabled={cancelDisabled ?? !dirty}
          className="edhdr-btn"
          data-testid={`${p}-cancel-btn`}
        >
          {t('editorHeader.cancel')}
        </Button>
        <Button
          size="small"
          icon={<UndoOutlined />}
          onClick={onReset}
          className="edhdr-btn"
          data-testid={`${p}-reset-btn`}
        >
          {t('editorHeader.resetDefault')}
        </Button>
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={saveDisabled}
          onClick={onSave}
          className="edhdr-btn edhdr-save-btn"
          data-testid={`${p}-save-btn`}
        >
          {saving ? t('editorHeader.saving') : saveLabel || t('editorHeader.save')}
        </Button>
        <Tooltip title={t('editorHeader.close')}>
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            className="edhdr-btn edhdr-close-btn"
            data-testid={`${p}-close-btn`}
            aria-label={t('editorHeader.close')}
          />
        </Tooltip>
      </div>
    </header>
  )
}

/** Shared discard confirmation — used by every editor's `requestClose()`. */
export function confirmDiscardAndClose(dirty: boolean, onClose: () => void, confirmMessage: string) {
  if (dirty && !window.confirm(confirmMessage)) return
  onClose()
}
